import * as React from 'react';

import { getDisplayName } from 'components/hoc';
import { delayed } from 'lib/Job';

import { push as dataLayerPush } from 'helper/ga';
import noop from 'helper/noop';

import { CardTypes } from 'defs/card-types';
import { Fields as FavoriteTrackingFields } from 'defs/ga/fields/favorite';
import { trackFavoriteAuto } from 'defs/ga/track/favorite';

export type FavoriteToggleParam = { id: string; favorite: boolean; type: string; externalId: string };

export type WithFavoriteStateType = {
	showFavoriteNotification: boolean;
};

export type WithFavoritesInternalPropsType = {
	id: string;
	favorite: boolean;
	type: string;
	contentTypeExternalId: string;
	title?: string;
	productId?: string;
	doToggleFavorite: (params: FavoriteToggleParam) => void;
};

export type WithFavoritePropsType = {
	showFavoriteNotification: boolean;
	handleToggleFavorite: (temporary?: boolean) => void;
};

export default function withFavorite<P extends object>(WrappedComponent: React.ComponentType<P>) {
	return class FavoriteProxy extends React.Component<P & WithFavoritesInternalPropsType, WithFavoriteStateType> {
		public static displayName = `withFavorite(${getDisplayName(WrappedComponent)})`;

		public state: WithFavoriteStateType = {
			showFavoriteNotification: false,
		};

		public forceSetState: (newState: WithFavoriteStateType, callback?: () => void) => void = undefined as any;

		public componentDidMount() {
			this.forceSetState = (newState: WithFavoriteStateType, callback?: () => void) =>
				this.setState(newState, callback);
		}

		public componentWillUnmount() {
			this.forceSetState = noop;
		}

		public handleToggleFavorite = () => {
			const { doToggleFavorite, id, favorite, type, contentTypeExternalId } = this.props;

			doToggleFavorite({
				id,
				favorite,
				type,
				externalId: contentTypeExternalId,
			} as any);

			this.trackFavorite();

			delayed(() => {
				this.forceSetState({ showFavoriteNotification: true }, () => {
					delayed(() => {
						this.forceSetState({ showFavoriteNotification: false });
					}, 3000);
				});
			}, 500);
		};

		public trackFavorite() {
			const { favorite, type, title, productId } = this.props;
			const favoriteTrackingProps = {
				[FavoriteTrackingFields.Type]: type,
				[FavoriteTrackingFields.Favorite]: !favorite,
				[FavoriteTrackingFields.ProductName]: title || '',
				[FavoriteTrackingFields.ProductId]: type === CardTypes.DynamicProduct ? productId || '' : ' ',
			};

			dataLayerPush(trackFavoriteAuto(favoriteTrackingProps as any));
		}

		public render() {
			const { showFavoriteNotification } = this.state;
			const internalPublicProps: WithFavoritePropsType = {
				showFavoriteNotification,
				handleToggleFavorite: this.handleToggleFavorite,
			};

			return <WrappedComponent {...this.props} {...internalPublicProps} />;
		}
	};
}
